import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Input } from '../../../components/ui/Input';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog';
import { EmptyState } from '../../../components/ui/EmptyState';
import { Spinner } from '../../../components/ui/Spinner';
import { useToast } from '../../../hooks/useToast';
import { AdminPlan, PlanGift, formatKobo, plansApi } from '../../../api/plans';
import { BusinessRow, businessesApi } from '../../../api/businesses';

const date = (iso: string) =>
  new Date(iso).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' });

const th: React.CSSProperties = {
  padding: '10px 12px',
  textAlign: 'left',
  fontSize: 12,
  color: 'var(--neutral-600)',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};
const td: React.CSSProperties = { padding: '10px 12px', fontSize: 13, verticalAlign: 'top' };
const fieldStyle: React.CSSProperties = {
  height: 38,
  padding: '0 10px',
  fontSize: 14,
  fontFamily: 'var(--font-family)',
  color: 'var(--neutral-800)',
  background: 'var(--neutral-100)',
  border: '1px solid var(--neutral-300)',
  borderRadius: 'var(--radius-md)',
};

/**
 * Give a business a paid plan for some months.
 *
 * The business sees it the next time it opens the app ("You're now on
 * Business for 3 months"), pays nothing while it runs, and its own plan's
 * renewal waits until it ends. Ending one early brings that renewal back by
 * whatever was left.
 */
export const Gifts: React.FC<{ plans: AdminPlan[]; canManage: boolean }> = ({ plans, canManage }) => {
  const toast = useToast();
  const giftable = plans.filter((p) => p.status === 'published' && p.monthlyPriceKobo > 0);

  const [gifts, setGifts] = useState<PlanGift[] | null>(null);
  const [filter, setFilter] = useState<'active' | ''>('active');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<BusinessRow[]>([]);
  const [searching, setSearching] = useState(false);
  const [business, setBusiness] = useState<BusinessRow | null>(null);
  const [planId, setPlanId] = useState(giftable[0]?.id ?? '');
  const [months, setMonths] = useState('3');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [ending, setEnding] = useState<PlanGift | null>(null);

  const load = useCallback(async () => {
    try {
      setGifts(await plansApi.gifts(filter || undefined));
    } catch (err) {
      toast.error(err, 'Could not load gifts.');
    }
  }, [filter, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  async function search() {
    if (!query.trim()) return;
    setSearching(true);
    try {
      setResults((await businessesApi.list({ search: query.trim(), limit: 8 })).items);
    } catch (err) {
      toast.error(err, 'Could not search businesses.');
    } finally {
      setSearching(false);
    }
  }

  const monthsN = Number(months);
  const monthsValid = Number.isInteger(monthsN) && monthsN >= 1 && monthsN <= 24;
  const plan = giftable.find((p) => p.id === planId);

  async function give() {
    if (!business || !plan || !monthsValid) return;
    setBusy(true);
    try {
      await plansApi.gift({ businessId: business.id, planId: plan.id, months: monthsN, ...(note.trim() ? { note: note.trim() } : {}) });
      toast.success(`${business.tradingName ?? business.name} is on ${plan.name} for ${monthsN} ${monthsN === 1 ? 'month' : 'months'}.`);
      setBusiness(null);
      setResults([]);
      setQuery('');
      setNote('');
      await load();
    } catch (err) {
      toast.error(err, 'Could not give that plan.');
    } finally {
      setBusy(false);
    }
  }

  async function end() {
    if (!ending) return;
    setBusy(true);
    try {
      await plansApi.endGift(ending.id);
      toast.success(`${ending.businessName}'s gift has ended. They have been told.`);
      setEnding(null);
      await load();
    } catch (err) {
      toast.error(err, 'Could not end that gift.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {canManage && (
        <Card>
          <h3 style={{ margin: '0 0 6px', fontSize: 15 }}>Give a plan</h3>
          <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--neutral-600)', maxWidth: 640 }}>
            The business sees it the next time it opens the app, pays nothing while it runs, and is told a week
            before it ends. If it already pays for a plan, that plan&apos;s next payment moves back by the same time.
          </p>

          {business ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <strong style={{ fontSize: 14 }}>{business.tradingName ?? business.name}</strong>
              <span style={{ fontSize: 12, color: 'var(--neutral-500)' }}>{business.email ?? business.phone}</span>
              <Button size="sm" variant="ghost" onClick={() => setBusiness(null)}>
                Change
              </Button>
            </div>
          ) : (
            <div style={{ marginBottom: 14 }}>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void search();
                }}
                style={{ display: 'flex', gap: 8, maxWidth: 480 }}
              >
                <input
                  aria-label="Find a business"
                  placeholder="Business name, email or phone"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  style={{ ...fieldStyle, flex: 1 }}
                />
                <Button type="submit" variant="secondary" loading={searching}>
                  Find
                </Button>
              </form>
              {results.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', marginTop: 8, maxWidth: 480 }}>
                  {results.map((b) => (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => setBusiness(b)}
                      style={{
                        textAlign: 'left',
                        padding: '8px 10px',
                        border: 'none',
                        borderBottom: '1px solid var(--neutral-200)',
                        background: 'transparent',
                        cursor: 'pointer',
                        fontFamily: 'var(--font-family)',
                      }}
                    >
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{b.tradingName ?? b.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>{b.email ?? b.phone ?? b.id}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, fontWeight: 500 }}>
              Plan
              <select aria-label="Plan" value={planId} onChange={(e) => setPlanId(e.target.value)} style={fieldStyle}>
                {giftable.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({formatKobo(p.monthlyPriceKobo)} a month)
                  </option>
                ))}
              </select>
            </label>
            <div style={{ width: 120 }}>
              <Input
                label="Months"
                type="number"
                min={1}
                max={24}
                value={months}
                onChange={(e) => setMonths(e.target.value)}
                error={months && !monthsValid ? '1 to 24' : undefined}
              />
            </div>
            <div style={{ flex: 1, minWidth: 220 }}>
              <Input
                label="Note (optional, admin only)"
                value={note}
                maxLength={500}
                placeholder="Why, for whoever reads this later"
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
            <Button onClick={() => void give()} loading={busy} disabled={busy || !business || !plan || !monthsValid}>
              Give plan
            </Button>
          </div>
          {plan && monthsValid && (
            <div style={{ marginTop: 10, fontSize: 12, color: 'var(--neutral-600)' }}>
              Worth {formatKobo(plan.monthlyPriceKobo * monthsN)} at the monthly price.
            </div>
          )}
        </Card>
      )}

      <Card padding={0}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px' }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>Gifts</h3>
          <div style={{ display: 'flex', gap: 6 }}>
            <Button size="sm" variant={filter === 'active' ? 'primary' : 'ghost'} onClick={() => setFilter('active')}>
              Running
            </Button>
            <Button size="sm" variant={filter === '' ? 'primary' : 'ghost'} onClick={() => setFilter('')}>
              All
            </Button>
          </div>
        </div>
        {gifts === null ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
            <Spinner />
          </div>
        ) : gifts.length === 0 ? (
          <EmptyState title={filter ? 'No gifts running.' : 'No gifts yet.'} />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: 760, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--neutral-200)' }}>
                  {['Business', 'Plan', 'From', 'Until', 'Status', ''].map((h) => (
                    <th key={h} style={th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {gifts.map((g) => (
                  <tr key={g.id} style={{ borderTop: '1px solid var(--neutral-300)' }}>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{g.businessName}</div>
                      {g.note && <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>{g.note}</div>}
                    </td>
                    <td style={td}>
                      {g.planName}, {g.months} {g.months === 1 ? 'month' : 'months'}
                    </td>
                    <td style={td}>{date(g.startsAt)}</td>
                    <td style={td}>{date(g.endedAt ?? g.endsAt)}</td>
                    <td style={td}>
                      <Badge variant={g.status === 'active' ? 'success' : 'neutral'}>
                        {g.status === 'active' ? 'Running' : g.status === 'ended' ? 'Ended early' : 'Finished'}
                      </Badge>
                    </td>
                    <td style={td}>
                      {canManage && g.status === 'active' && (
                        <Button size="sm" variant="ghost" onClick={() => setEnding(g)}>
                          End now
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <ConfirmDialog
        isOpen={!!ending}
        title="End this gift now?"
        message={`${ending?.businessName ?? 'The business'} goes back to its own plan straight away and is told. If it pays for a plan, its next payment comes forward by the time that was left.`}
        confirmLabel="End gift"
        variant="danger"
        loading={busy}
        onConfirm={() => void end()}
        onCancel={() => setEnding(null)}
      />
    </div>
  );
};
